<div class="campaigns-container">
  <app-datatable
    [rowData]="dataSource().data"
    [columns]="datatableColumns"
    [configurationInput]="datatableConfig"
    [customColumnDef]="[
      { name: 'name', isSortable: true, template: nameColumn },
      { name: 'status', isSortable: true, template: statusColumn },
      { name: 'startFormatted', isSortable: true, template: startColumn },
      { name: 'endFormatted', isSortable: true, template: endColumn },
      { name: 'customer', isSortable: false, template: customerColumn },
      { name: 'budget', isSortable: false, template: budgetColumn },
      { name: 'pay', isSortable: false, template: payColumn },
      { name: 'ctaKpiStatus', isSortable: false, template: ctaKpiStatusColumn },
      { name: 'actions', isSortable: false, template: actionsColumn },
    ]"
    [total]="total()"
    [loaderConfig]="skeletonLoaderConfig"
    [isInitialDataLoaded]="loadedTableData()"
    [loadingData]="campaignsLoading()"
    [trackBy]="trackByCampaignId"
    (dataRefreshRequest)="onDataRefreshRequest($event)"
    (rowClick)="onRowClick($event)"
  />

  <ng-template #nameColumn let-row="row" let-index="index">
    <div class="flex items-center gap-2">
      <app-icon-badge
        alt="Campaign"
        [content]="row.iconId"
        [fallbackBackgroundColor]="index % 2 === 0 ? 'var(--shikenso-base-white)' : 'var(--shikenso-gray-base)'"
        [fallbackText]="row.name"
        [size]="40"
        [iconSize]="40"
        [fontSize]="16"
        [borderRadius]="100"
      />
      <span matTooltip class="font-bold line-clamp-1 text-ellipsis" [appTextClampTooltip]="row.name">
        {{ row.name }}
      </span>
    </div>
  </ng-template>

  <ng-template #statusColumn let-row="row">
    <app-status-badge [status]="row.status" />
  </ng-template>

  <ng-template #startColumn let-row="row">
    <div class="flex items-center justify-start gap-2">
      <app-icon src="/icons/calendar.svg" alt="Date" class="w-4 h-4" />
      <div class="flex items-center">
        <span>{{ row.startFormatted }}</span>
      </div>
    </div>
  </ng-template>

  <ng-template #endColumn let-row="row">
    <div class="flex items-center justify-start gap-2">
      <app-icon src="/icons/calendar.svg" alt="Date" class="w-4 h-4" />
      <div class="flex items-center">
        <span>{{ row.endFormatted }}</span>
      </div>
    </div>
  </ng-template>

  <ng-template #customerColumn let-row="row" let-index="index">
    @if (row.customer) {
      <div class="flex items-center gap-2" (click)="onCustomerClick($event, row.customer)">
        <app-icon-badge
          alt="Customer"
          [content]="row.customer.iconId"
          [fallbackBackgroundColor]="index % 2 === 0 ? 'var(--shikenso-base-white)' : 'var(--shikenso-gray-base)'"
          [fallbackText]="row.customer.name"
          [size]="16"
          [iconSize]="16"
          [fontSize]="8"
          [borderRadius]="50"
        />
        <span matTooltip class="line-clamp-2 text-ellipsis" [appTextClampTooltip]="row.customer.name">
          {{ row.customer.name }}
        </span>
      </div>
    }
  </ng-template>

  <ng-template #ctaKpiStatusColumn let-row="row">
    @if (isFetchingAnalytics) {
      <ngx-skeleton-loader class="flex h-2" [theme]="{ width: '108px', height: '8px' }" />
    } @else {
      <div class="flex w-fit">
        <app-progress-bar
          [current]="row.ctaKpiStatus.current"
          [total]="row.ctaKpiStatus.total"
          [currentColor]="row.ctaKpiStatus.color"
          [isProgressLabelVisible]="false"
          [minWidth]="108"
          [showProgressTooltip]="true"
          [showPercentageTooltip]="true"
        />
      </div>
    }
  </ng-template>

  <ng-template #payColumn let-row="row">
    <div class="flex items-center">
      <span>{{ row.pay | numberSuffix }} {{ row.currency }}</span>
    </div>
  </ng-template>

  <ng-template #budgetColumn let-row="row">
    <div class="flex items-center">
      <span>{{ row.budget | numberSuffix }} {{ row.currency }}</span>
    </div>
  </ng-template>

  <ng-template #actionsColumn let-row="row">
    <div
      *hasPermission="[
        EPermissions.campaignUpdateOwn,
        EPermissions.campaignUpdateAll,
        EPermissions.campaignCreate,
        EPermissions.campaignDeleteOwn,
      ]"
      class="flex items-center justify-start"
    >
      <button
        mat-icon-button
        class="mat-secondary-transparent-button-color"
        [matMenuTriggerFor]="menu"
        (click)="$event.stopPropagation()"
      >
        <app-icon src="/icons/three-dots.svg" alt="More" [size]="16" />
      </button>
      <mat-menu #menu="matMenu">
        <button
          *hasPermission="[EPermissions.campaignUpdateOwn, EPermissions.campaignUpdateAll]"
          mat-menu-item
          (click)="onEditClick($event, row)"
        >
          {{ 'shared.form.edit' | translate }}
        </button>
        <button
          *hasPermissionGroups="[
            [EPermissions.campaignUpdateOwn, EPermissions.campaignCreate],
            [EPermissions.campaignUpdateAll, EPermissions.campaignCreate],
          ]"
          mat-menu-item
          (click)="onCopyClick($event, row)"
        >
          {{ 'pages.campaigns.datatable.copyCampaign' | translate }}
        </button>
        <button *hasPermission="[EPermissions.campaignDeleteOwn]" mat-menu-item (click)="onDeleteClick($event, row)">
          {{ 'shared.form.delete' | translate }}
        </button>
      </mat-menu>
    </div>
  </ng-template>

  <ng-template #createdByFilter>
    @if (usersLoading()) {
      <ngx-skeleton-loader [theme]="{ height: '34px', width: '220px', 'border-radius': '100vw' }" />
    } @else {
      <mat-form-field class="custom-form !min-w-[220px]">
        <mat-label>{{ 'field.createdBy' | translate }}</mat-label>
        <mat-select
          multiple
          [formControl]="selectionControl"
          [hideSingleSelectionIndicator]="true"
          [placeholder]="emptyLabel()"
          panelWidth="320px"
          (selectionChange)="onSelectionChange($event)"
        >
          <mat-select-trigger>
            <span>{{ triggerLabel() }}</span>
          </mat-select-trigger>

          <mat-option class="contains-mat-select-search">
            <ngx-mat-select-search
              [formControl]="searchControl"
              [placeholderLabel]="'shared.search' | translate"
              [noEntriesFoundLabel]="''"
            >
              <app-icon ngxMatSelectSearchClear src="icons/close.svg" alt="close" [size]="16" />
            </ngx-mat-select-search>
          </mat-option>

          <div class="flex items-center" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
            <button
              mat-flat-button
              color="secondary-transparent"
              class="mat-slim-button"
              type="button"
              (click)="onSelectAll($event)"
            >
              {{ 'shared.selectAll' | translate }}
            </button>
            <button
              mat-flat-button
              color="secondary-transparent"
              class="mat-slim-button"
              type="button"
              (click)="onUnselectAll($event)"
            >
              {{ 'shared.unselectAll' | translate }}
            </button>
          </div>

          @for (user of displayedUsers(); track user.id) {
            <mat-option [value]="user.id">
              <div class="flex items-center gap-2 min-w-0">
                <app-icon-badge
                  [content]="user.iconId"
                  [fallbackText]="user.name"
                  [size]="24"
                  [iconSize]="24"
                  [fontSize]="14"
                />
                <span class="truncate">{{ user.name }}</span>
              </div>
            </mat-option>
          }

          @if (searchQuery() && displayedUsers().length === 0) {
            <mat-option class="no-results-option" disabled>{{ 'shared.noResults' | translate }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }
  </ng-template>

  <ng-template #teamFilter>
    @if (teamsLoading()) {
      <ngx-skeleton-loader [theme]="{ height: '34px', width: '220px', 'border-radius': '100vw' }" />
    } @else {
      <mat-form-field class="custom-form !min-w-[220px]">
        <mat-label>{{ 'field.team' | translate }}</mat-label>
        <mat-select
          multiple
          [formControl]="teamSelectionControl"
          [hideSingleSelectionIndicator]="true"
          [placeholder]="teamEmptyLabel()"
          panelWidth="320px"
          (selectionChange)="onTeamSelectionChange($event)"
        >
          <mat-select-trigger>
            <span>{{ teamTriggerLabel() }}</span>
          </mat-select-trigger>

          <mat-option class="contains-mat-select-search">
            <ngx-mat-select-search
              [formControl]="teamSearchControl"
              [placeholderLabel]="'shared.search' | translate"
              [noEntriesFoundLabel]="''"
            >
              <app-icon ngxMatSelectSearchClear src="icons/close.svg" alt="close" [size]="16" />
            </ngx-mat-select-search>
          </mat-option>

          <div class="flex items-center" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
            <button
              mat-flat-button
              color="secondary-transparent"
              class="mat-slim-button"
              type="button"
              (click)="onTeamSelectAll($event)"
            >
              {{ 'shared.selectAll' | translate }}
            </button>
            <button
              mat-flat-button
              color="secondary-transparent"
              class="mat-slim-button"
              type="button"
              (click)="onTeamUnselectAll($event)"
            >
              {{ 'shared.unselectAll' | translate }}
            </button>
          </div>

          @for (team of displayedTeams(); track team.id) {
            <mat-option [value]="team.id">
              <div class="flex items-center gap-2 min-w-0">
                <app-icon-badge
                  [content]="team.iconId ?? null"
                  [fallbackText]="team.name"
                  [size]="24"
                  [iconSize]="24"
                  [fontSize]="14"
                />
                <span class="truncate">{{ team.name }}</span>
              </div>
            </mat-option>
          }

          @if (teamSearchQuery() && displayedTeams().length === 0) {
            <mat-option class="no-results-option" disabled>{{ 'shared.noResults' | translate }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }
  </ng-template>
</div>
